import React from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';

import { AmountAndCurrencyFieldDesktop } from '../amount-and-currency-field/AmountAndCurrencyField';
import ConvertRateInfoLine from '../convert-rate-info-line/ConvertRateInfoLine';
import ExchangeRateOneToOneInfo from '../exchange-rate-one-to-one-info/ExchangeRateOneToOneInfo';
import SwapButton from '../swap-button/SwapButton';
import { CalculatorFieldName, Piwik, SiteCoreDictionary, VisaFxSimpleCalculatorProps } from '../types';
import ReadMoreCard from './read-more-card/ReadMoreCard';

import './VisaFxSimpleCalculatorDesktop.scss';

type SwapButtonAndLoaderContainerProps = {
    loadingRate: boolean;
    piwik: Piwik | undefined;
    swapCurrencies: () => void;
};

const SwapButtonAndLoaderContainer = ({ piwik, swapCurrencies, loadingRate }: SwapButtonAndLoaderContainerProps) => (
    <div className="swap-button-loader-container">
        <span className="spanner" />
        <span className="double-spanner" />
        <div className="swap-button-loader-indicator">
            <div className="swap-button">
                {loadingRate ? (
                    <Loader className="rate-loader" mini />
                ) : (
                    <SwapButton swapCurrencies={swapCurrencies} piwik={piwik} />
                )}
            </div>
        </div>
    </div>
);

type HeaderDisplayProps = {
    dictionary: SiteCoreDictionary;
};

const HeaderDisplay = ({ dictionary }: HeaderDisplayProps) => {
    const { visaCalculatorMainHeader, visaCalculatorMainDescription } = dictionary;

    return (
        <header className="calculator-header">
            <h1>{visaCalculatorMainHeader}</h1>
            <p>{visaCalculatorMainDescription}</p>
        </header>
    );
};

const VisaFxSimpleCalculatorDesktop = ({
    calculator,
    loadingRate,
    onChangeAmountFrom,
    onChangeAmountTo,
    onClickMoreInfo,
    onClickSelectCurrency,
    rateError,
    setFocusedField,
    swapCurrencies,
    currencyOnRightSide,
    dictionary,
    piwik,
}: VisaFxSimpleCalculatorProps) => {
    return (
        <div className="calculator-container">
            <article className="calculator-with-swap-button">
                <HeaderDisplay dictionary={dictionary} />
                <div className="from-to-currency-container">
                    <ExchangeRateOneToOneInfo calculator={calculator} />
                    <AmountAndCurrencyFieldDesktop
                        calculator={calculator}
                        focusedField={CalculatorFieldName.FROM}
                        onChangeAmount={onChangeAmountFrom}
                        onClickSelectCurrency={onClickSelectCurrency}
                        setFocusedField={setFocusedField}
                        currencyOnRightSide={currencyOnRightSide}
                        dictionary={dictionary}
                        piwik={piwik}
                    />
                    <SwapButtonAndLoaderContainer
                        loadingRate={loadingRate}
                        swapCurrencies={swapCurrencies}
                        piwik={piwik}
                    />
                    <AmountAndCurrencyFieldDesktop
                        calculator={calculator}
                        focusedField={CalculatorFieldName.TO}
                        onChangeAmount={onChangeAmountTo}
                        onClickSelectCurrency={onClickSelectCurrency}
                        setFocusedField={setFocusedField}
                        currencyOnRightSide={currencyOnRightSide}
                        dictionary={dictionary}
                        piwik={piwik}
                    />
                </div>
                {rateError && (
                    <div className="error-message-container">
                        <ErrorMessage text={rateError} icon={IconTypes.LUKK_24} />
                    </div>
                )}
                <footer className="footer-info">
                    <ConvertRateInfoLine onClickMoreInfo={onClickMoreInfo} dictionary={dictionary} piwik={piwik} />
                </footer>
            </article>
            <div className="read-more-container">
                <ReadMoreCard onClickMoreInfo={onClickMoreInfo} dictionary={dictionary} piwik={piwik} />
            </div>
        </div>
    );
};

export default VisaFxSimpleCalculatorDesktop;
